import { Component, DestroyRef, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { combineLatest, distinctUntilChanged, filter, shareReplay, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerDataUI, JourneyMetadataUI } from 'src/app/interfaces/customer-data.interface';
import { GenerateSendOtpUI } from 'src/app/interfaces/generate-send-otp.interface';
import { InitialParametersUI } from 'src/app/interfaces/initial-parameters.interface';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { TimerService } from 'src/app/services/otp/timer.service';
import { SnackbarService } from 'src/app/services/snackbars/snackbar.service';
import { StoreService } from 'src/app/services/store/store.service';
import { MAX_OTP_DIGITS, Patterns } from 'src/app/utils/constants';
import { SnackbarMessageKey } from 'src/app/utils/enums';
import { buildRequestGenerateOtp, buildUrlSimulator } from 'src/app/utils/utils';
import { GtmService } from 'src/app/services/gtm/gtm.service';
import { ClickEventTags, PageEventTags } from 'src/app/services/gtm/gtm-types';

@Component({
	selector: 'app-verify-phone',
	templateUrl: './verify-phone.component.html',
	styleUrls: ['./verify-phone.component.scss'],
})
export class VerifyPhoneComponent implements OnInit {
	readonly vm$ = combineLatest({
		customerData: this.storeService.customerData$,
		journeyMetadata: this.storeService.journeyMetadata$,
		parameters: this.storeService.initialParameters$,
		expired: this.timerService.expiredOtp$,
		timer: this.timerService.displayTime$,
		otp: this.storeService.otp$,
		loaderButton: this.loaderService.loaderButton$,
	}).pipe(
		filter(
			(
				vm
			): vm is {
				customerData: CustomerDataUI;
				journeyMetadata: JourneyMetadataUI;
				parameters: InitialParametersUI;
				expired: boolean;
				timer: string;
				otp: GenerateSendOtpUI;
				loaderButton: boolean;
			} =>
				vm.customerData != null && vm.journeyMetadata != null && vm.parameters != null && vm.otp != null
		),
		shareReplay(1)
	);

	otpForm = this.fb.nonNullable.group({
		digits: this.fb.array<FormControl<string>>(
			Array.from({ length: MAX_OTP_DIGITS }, () =>
				this.fb.nonNullable.control('', {
					validators: [
						Validators.required,
						Validators.maxLength(1),
						Validators.pattern(Patterns.oneDigit),
					],
				})
			)
		),
	});

	constructor(
		private readonly storeService: StoreService,
		private readonly loaderService: LoaderService,
		private readonly timerService: TimerService,
		private readonly snackbarService: SnackbarService,
		private readonly modalService: ModalService,
		private readonly fb: FormBuilder,
		private readonly destroyRef: DestroyRef,
		private readonly gtmService: GtmService
	) {}

	ngOnInit(): void {
		this.timerService.expiredOtp$
			.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
			.subscribe(expired => {
				if (expired) {
					this.snackbarService.show(SnackbarMessageKey.OTP_EXPIRED);
				}
			});

		this.gtmService.handlerEvent(PageEventTags.dataValidation);
	}

	get digits(): FormArray<FormControl<string>> {
		return this.otpForm.controls.digits;
	}

	getOtpCode(): string {
		return this.digits.controls.map(c => c.value).join('');
	}

	onValidateOtp(
		companyRut: string,
		timeUp: boolean,
		journey: JourneyMetadataUI,
		simulatorUrl: string
	): void {
		if (this.otpForm.invalid || timeUp) return;

		this.gtmService.handlerEvent(ClickEventTags.verifyOtp);

		this.storeService
			.validationOtp({
				rutCliente: companyRut,
				codigoOtp: this.getOtpCode(),
			})
			.pipe(
				take(1),
				tap(validate => {
					if (validate.otpValid) {
						this.loaderService.show();
						window.location.assign(buildUrlSimulator(simulatorUrl, journey.uuid!, journey.id!));
						return;
					}

					this.snackbarService.show(SnackbarMessageKey.VALIDATION_ERROR, validate.attempts);
					this.resetOtpControls();
				})
			)
			.subscribe();
	}

	private resetOtpControls(): void {
		this.digits.controls.forEach(ctrl => {
			ctrl.reset('', { emitEvent: false });
			ctrl.markAsTouched();
		});
	}

	onResendOtp(companyRut: string, timeUp: boolean): void {
		if (!timeUp) return;
		this.gtmService.handlerEvent(ClickEventTags.resendCode);

		this.storeService
			.generateSendOtp(buildRequestGenerateOtp(companyRut), true)
			.pipe(
				take(1),
				tap(() => this.snackbarService.show(SnackbarMessageKey.OTP_SENT))
			)
			.subscribe();
	}

	onUpdatePhone(): void {
		this.gtmService.handlerEvent(ClickEventTags.editPhone);
		this.modalService.openPhoneUpdateModal();
	}
}
